import {
    ObjectTagValuesTagGroupConfiguration,
    ObjectTagValuesTagGroupTagConfiguration,
    ObjectTagValuesConfiguration,
    ObjectTagValuesGridColumnConfiguration
} from "../../../services/object-tag-values-data-service/ConfigurationModels";
import {
    ObjectTagValuesTagGroupConfigurationFieldIsDirtyType,
    ObjectTagValuesTagGroupConfigurationFieldType,
    ObjectTagValuesTagGroupTagConfigurationFieldIsDirtyType,
    ObjectTagValuesTagGroupTagConfigurationFieldType,
    ObjectTagValuesConfigurationFieldIsDirtyType,
    ObjectTagValuesConfigurationFieldType,
    ObjectTagValuesGridColumnConfigurationFieldIsDirtyType,
    ObjectTagValuesGridColumnConfigurationFieldType
} from "./ObjectTagValuesConfigurationFieldTypes";
import { ColumnType } from "../../../common/enums/DRCXEnums";
import { DataTypeEnum } from "../../../common/enums/DataTypeEnum";
import { makeSelectedTagEquatableKey } from "../../../common/utilities/drcx/MakeSelectedTagEquatableKey";

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Validation Functions

export function allTagGroupsValid(tagGroups: Array<ObjectTagValuesTagGroupConfigurationFieldType> | undefined): boolean {
    return tagGroups?.every(tagGroupIsValid) ?? false;
}

export function tagGroupIsValid(tagGroup: ObjectTagValuesTagGroupConfigurationFieldType | undefined): boolean {
    return (tagGroup?.name?.length ?? 0) > 0 && selectedTagsIsValid(tagGroup);
}

export function selectedTagsIsValid(
    tagGroup: ObjectTagValuesTagGroupConfigurationFieldType | undefined
): boolean {
    return (tagGroup?.tags?.length ?? 0) > 0;
}

// #endregion Validation Functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Merge Functions

export function mergeConfigurations(
    configurationModel: ObjectTagValuesConfiguration,
    configurationField: ObjectTagValuesConfigurationFieldType
): ObjectTagValuesConfiguration {

    const configuration: ObjectTagValuesConfiguration = {
        ...configurationModel,
        id: configurationField.id,
        name: configurationField.name,
        access: configurationField.access, // TODO: Do I need this in the field?

        // TODO: Keep this simple for now...need an id for the fullouterjoin routine        
        tagGroups: configurationField.tagGroups.map(tagGroup => ({
            ...tagGroup,
            tags: tagGroup.tags.map(tag => ({
                ...tag,
                // tatID: tag.tatID ?? undefined,
                // tatTypeID: tag.tatTypeID ?? undefined,
            }))
        })),
        gridColumns: configurationField.gridColumns.map(column => ({
            ...column,
            // name: column.name,
            // type: column.type
        }))
    };

    return configuration;
}

// #endregion Merge Functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Field conversion functions

export function toConfigurationField(
    duplicate: boolean | undefined,
    configuration: ObjectTagValuesConfiguration
): ObjectTagValuesConfigurationFieldType {
    return {
        id: duplicate ? -1 : configuration.id,
        name: duplicate ? `${configuration.name} (copy)` : configuration.name,
        access: configuration.access,
        tagGroups: configuration.tagGroups.map(toTagGroupField),
        gridColumns: configuration.gridColumns.map(toGridColumnField)
    };
}

function toTagGroupField(
    tagGroup: ObjectTagValuesTagGroupConfiguration
): ObjectTagValuesTagGroupConfigurationFieldType {
    return {
        name: tagGroup.name,
        tags: tagGroup.tags.map(toTagGroupTagField)
    };
}

function toTagGroupTagField(
    tag: ObjectTagValuesTagGroupTagConfiguration
): ObjectTagValuesTagGroupTagConfigurationFieldType {
    return {
        // tatID: tag.tatID,
        // tatName: tag.tatName,
        // tatTypeID: tag.tatTypeID,
        // tatTypeName: tag.tatTypeName,
        ...tag,
        equatableKey: makeSelectedTagEquatableKey(
            tag.tatTypeID ? ColumnType.TatType : ColumnType.Tat,
            tag.tatID,
            tag.tatTypeID,
            DataTypeEnum.Double
        ),
        isNew: false
    };
}

function toGridColumnField(
    column: ObjectTagValuesGridColumnConfiguration
): ObjectTagValuesGridColumnConfigurationFieldType {
    return {
        // name: column.name,
        // type: column.type
        ...column
    };
}

// #endregion Field conversion functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Tag Group Functions

export function createDefaultTagGroupField(name: string): ObjectTagValuesTagGroupConfigurationFieldType {
    return {
        name: name,
        tags: new Array<ObjectTagValuesTagGroupTagConfigurationFieldType>()
    };
}

// #endregion Tag Group Functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region IsDirty Functions

export function configurationFieldIsDirty(dirtyFields: ObjectTagValuesConfigurationFieldIsDirtyType): boolean {
    return (
        dirtyFields?.name === true ||
        dirtyFields?.tagGroups?.some(tagGroupFieldIsDirty) ||
        dirtyFields?.gridColumns?.some(gridColumnFieldIsDirty)
    ) ?? false;
}

export function tagGroupFieldIsDirty(dirtyFields?: ObjectTagValuesTagGroupConfigurationFieldIsDirtyType): boolean {
    return (
        dirtyFields?.name === true ||
        dirtyFields?.tags?.some(tagFieldIsDirty)
    ) ?? false
}

export function tagFieldIsDirty(tag?: ObjectTagValuesTagGroupTagConfigurationFieldIsDirtyType): boolean {
    return tag?.tatID === true ||
        tag?.tatTypeID === true;
}

export function gridColumnFieldIsDirty(dirtyFields?: ObjectTagValuesGridColumnConfigurationFieldIsDirtyType): boolean {
    return dirtyFields?.name === true ||
        dirtyFields?.type === true;
}

// #endregion IsDirty Functions
